import invariant from 'invariant';
import React, { ReactNode } from 'react';

import { BlockType, PageContentBlock, SidebarContentBlock } from 'api/cms';
import { PublicPage } from 'api/website';
import { AnchorBlock } from 'cms/blocks/Anchor/AnchorBlock';
import { blockMappingByType } from 'cms/blocks/blockMappingByType';
import {
  getContentHasBackground,
  getIsContentFullWidthLayout,
} from 'cms/helpers';
import { Box } from 'components/Box';

type Props = {
  content: PublicPage;
  blocks?: Array<PageContentBlock>;
  block: PageContentBlock | SidebarContentBlock;
  isSidebar: boolean;
  isPageHeader: boolean;
  anchor: PageContentBlock | undefined;
  offsetAnchors: boolean;
  index: number;
};

export type MappedContentBlock = {
  component: ReactNode;
  id: string;
  type: BlockType;
  width: 'FULL' | 'GRID' | 'INSET' | 'NARROW';
  padding?: 'DEFAULT' | 'NONE';
  mustNotHaveDivider: boolean;
  hasBackground?: boolean;
  isHidden?: boolean;
  isInline?: boolean;
};

export function mapContentBlock({
  content,
  blocks,
  block,
  isSidebar,
  isPageHeader,
  anchor,
  offsetAnchors,
  index,
}: Props): MappedContentBlock {
  const blockData = blockMappingByType[block.type];
  if (!blockData) throw new Error(`Expected block type, got ${block.type}`);

  const { width, Component, ...contentBlock } = blockData;
  contentBlock.invariant.forEach((item) => {
    if (block.data[item.prop]) {
      invariant(block.data[item.prop], item.msg);
    }
  });

  const widthValue = typeof width === 'function' ? width(block.data) : width;
  const fullWidthLayout = getIsContentFullWidthLayout({ content });
  const blockHasBackground = getContentHasBackground({
    block,
    fullWidthLayout,
  });

  return {
    id: block.id,
    type: block.type,
    width: widthValue,
    mustNotHaveDivider:
      block.type === 'SPLIT_CONTENT' && block.data.appearance === 'FULL_WIDTH',
    padding:
      (blocks &&
        ((block.type === 'SPLIT_CONTENT' &&
          block.data.appearance === 'FULL_WIDTH') ||
          [
            'GRAD_CTA_HERO',
            'CTA_HERO',
            'STICKY_HEADER_LINKS_GRAD',
            'STICKY_HEADER_LINKS',
            'DONATE',
            'SIGN_UP',
            'SCROLL_TRACKING',
          ].includes(block.type))) ||
      blockHasBackground
        ? 'NONE'
        : 'DEFAULT',
    hasBackground: blockHasBackground,
    isHidden: block.type === 'FREE_CODE' && !block.data.isVisible,
    isInline: block.type === 'SCROLL_TRACKING',
    component: (
      <>
        {anchor && anchor.type === 'ANCHOR' && (
          <Box
            position="absolute"
            top={offsetAnchors ? [0, null, null, -56] : 0}
          >
            <AnchorBlock data={anchor.data} />
          </Box>
        )}
        <div>
          <Component
            content={content}
            isSidebar={isSidebar}
            isPageHeader={
              isPageHeader &&
              ['CTA_HERO', 'GRAD_CTA_HERO', 'DONATE'].includes(block.type)
            }
            data={block.data}
            pageUpdated={content.updated}
            blockId={block.id}
            blockIndex={index}
          />
        </div>
      </>
    ),
  };
}
